import { useState, useEffect } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { apiGet } from '../../lib/api.js';
import { apiPatch } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import { Select } from '../Select.js';
import { ENGINE_LABELS } from '../../types/index.js';
import type { Tenant, TenantResponse } from '../../types/index.js';

const ALL_ENGINES = Object.keys(ENGINE_LABELS);

interface EngineModels {
  chatgpt: string;
  gemini: string;
  claude: string;
  google_aio: string;
  mistral: string;
}

// Spec 254: the model lists come from the server's catalogue
// (GET /api/v1/engine-models), checked against each provider's own model list.
// They used to be hard-coded here and rotted: Gemini offered only retired 2.5
// models, and two ChatGPT options never existed.
interface CatalogModel { id: string; label: string }
interface EngineCatalog { default: string; models: CatalogModel[]; legacy: string[] }
type ModelCatalog = Record<string, EngineCatalog>;

const PLAN_COLORS: Record<string, string> = {
  starter: 'bg-gray-100 text-gray-700',
  pro: 'bg-violet-100 text-violet-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

export function TenantSettingsPanel({ tenantId }: { tenantId: string }) {
  const { addToast } = useToast();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queryFrequency, setQueryFrequency] = useState<number>(7);
  const [samplesPerPrompt, setSamplesPerPrompt] = useState<number>(1);
  const [defaultEngines, setDefaultEngines] = useState<Set<string>>(new Set(ALL_ENGINES));
  const [engineModels, setEngineModels] = useState<Partial<EngineModels>>({});
  // What the tenant had saved, to say plainly when a saved model is no longer
  // offered (collection then uses the engine's default — the server does the same).
  const [savedModels, setSavedModels] = useState<Partial<EngineModels>>({});
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null);

  useEffect(() => {
    async function loadTenant() {
      try {
        const res = await apiGet<TenantResponse>(`/api/v1/tenants/${tenantId}`);
        setTenant(res.data);
        if (res.data.settings?.query_frequency != null) {
          setQueryFrequency(res.data.settings.query_frequency);
        }
        if (res.data.settings?.default_engines) {
          setDefaultEngines(new Set(res.data.settings.default_engines));
        }
        if (res.data.settings?.samples_per_prompt != null) {
          setSamplesPerPrompt(res.data.settings.samples_per_prompt as number);
        }
        const cat = await apiGet<{ data: ModelCatalog }>('/api/v1/engine-models');
        setCatalog(cat.data);
        const saved = (res.data.settings?.engine_models ?? {}) as Partial<EngineModels>;
        setSavedModels(saved);
        // Each engine starts on its saved model if still offered, else its default.
        const start: Partial<EngineModels> = {};
        for (const [engine, c] of Object.entries(cat.data)) {
          const s = saved[engine as keyof EngineModels];
          const offered = !!s && (c.models.some((m) => m.id === s) || c.legacy.includes(s));
          start[engine as keyof EngineModels] = offered ? s : c.default;
        }
        setEngineModels(start);
      } catch {
        addToast('Failed to load tenant settings', 'error');
      } finally {
        setLoading(false);
      }
    }
    loadTenant();
  }, [tenantId, addToast]);

  function toggleEngine(engine: string) {
    setDefaultEngines((prev) => {
      const next = new Set(prev);
      if (next.has(engine)) {
        // Don't allow deselecting all engines
        if (next.size <= 1) return prev;
        next.delete(engine);
      } else {
        next.add(engine);
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await apiPatch<TenantResponse>(`/api/v1/tenants/${tenantId}`, {
        settings: {
          ...tenant?.settings,
          query_frequency: queryFrequency,
          samples_per_prompt: samplesPerPrompt,
          default_engines: Array.from(defaultEngines),
          engine_models: engineModels,
        },
      });
      setTenant(res.data);
      addToast('Settings saved successfully', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="text-gray-400" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load tenant settings.
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      {/* Plan */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Plan</label>
        <div className="mt-1">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium capitalize ${
              PLAN_COLORS[tenant.plan] ?? PLAN_COLORS.starter
            }`}
          >
            {tenant.plan}
          </span>
          <p className="mt-1 text-xs text-gray-400">
            Contact your account manager to change plans.
          </p>
        </div>
      </div>

      {/* Query Frequency */}
      <Field
        label="Query frequency"
        help="Number of queries per week per prompt template."
        type="number"
        min={1}
        max={100}
        value={queryFrequency}
        onChange={(e) => setQueryFrequency(Math.max(1, parseInt(e.target.value, 10) || 1))}
        className="w-32"
      />

      {/* Samples per Prompt (071) */}
      <Field
        label="Samples per prompt"
        help="Number of replicate samples per prompt/engine per collection run. Higher values provide better statistical confidence. Recommended: 1 for testing, 20 for production."
        type="number"
        min={1}
        max={100}
        value={samplesPerPrompt}
        onChange={(e) => setSamplesPerPrompt(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
        className="w-32"
        data-testid="samples-per-prompt-input"
      />

      {/* Default Engines */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default engines
        </label>
        <p className="text-xs text-gray-400">
          Engines used for new query jobs. At least one must be selected.
        </p>
        <div className="mt-2 space-y-2">
          {ALL_ENGINES.map((engine) => (
            <label key={engine} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={defaultEngines.has(engine)}
                onChange={() => toggleEngine(engine)}
                className="h-4 w-4 rounded-md border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-sm text-gray-700">
                {ENGINE_LABELS[engine] ?? engine}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Engine Models */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Engine models
        </label>
        <p className="text-xs text-gray-400">
          Select which model to use for each AI engine.
        </p>
        <div className="mt-2 space-y-3">
          {(['chatgpt', 'gemini', 'claude', 'google_aio', 'mistral'] as const).map((engine) => {
            const c = catalog?.[engine];
            const saved = savedModels[engine];
            const stale = !!c && !!saved && !c.models.some((m) => m.id === saved) && !c.legacy.includes(saved);
            const defaultLabel = c?.models.find((m) => m.id === c.default)?.label.replace(/ \(.*\)$/, '') ?? c?.default;
            return (
              <div key={engine} className="flex flex-wrap items-center gap-3">
                <span className="w-24 text-sm text-gray-700">{ENGINE_LABELS[engine]}</span>
                {!c ? (
                  <Spinner />
                ) : c.models.length === 1 ? (
                  <span className="text-sm text-[var(--hub-color-ink-muted)]" data-testid={`model-select-${engine}`}>{c.models[0].label}</span>
                ) : (
                  <Select
                    // A saved choice that is no longer offered but still served
                    // stays selectable, so the picker shows what is in use
                    // rather than "Select…".
                    options={[
                      ...c.models.map((m) => ({ value: m.id, label: m.label })),
                      ...(saved && c.legacy.includes(saved) ? [{ value: saved, label: `${saved} (earlier choice, still served)` }] : []),
                    ]}
                    value={engineModels[engine] ?? c.default}
                    onChange={(v) => setEngineModels((prev) => ({ ...prev, [engine]: v }))}
                    className="w-80"
                    data-testid={`model-select-${engine}`}
                  />
                )}
                {stale && (
                  <p className="w-full pl-[6.75rem] text-xs text-[var(--hub-color-ink-muted)]" data-testid={`model-stale-${engine}`}>
                    Your saved model, <code className="hub-code">{saved}</code>, is no longer offered — collection uses {defaultLabel}. Save to update it.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Save button */}
      <div className="pt-2">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner /> : <Icon name="bx-save" />}
          Save settings
        </Button>
      </div>

      {/* Data Management */}
      <div className="mt-8 border-t border-red-200 pt-6">
        <h3 className="text-sm font-semibold text-red-700">Danger zone</h3>
        <p className="mt-1 text-xs text-slate-500">
          Reset all collected data (queries, responses, analyses, scores, gaps, playbooks, suggestions) for all brands. Brand configurations and prompt templates are preserved.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <Button
            variant="danger"
            onClick={async () => {
              if (!tenantId) return;
              if (!window.confirm('This will DELETE all collected data for ALL brands. Prompt templates and brand settings are kept. Continue?')) return;
              try {
                const { apiFetch } = await import('../../lib/api.js');
                const res = await apiFetch(`/api/v1/tenants/${tenantId}/data/reset`, { method: 'POST' });
                if (!res.ok) throw new Error('Reset failed');
                const result = await res.json() as { data: { total: number; brands_reset: number } };
                addToast(`Data reset: ${result.data.total} records deleted across ${result.data.brands_reset} brands`, 'success');
              } catch (err) {
                addToast(err instanceof Error ? err.message : 'Reset failed', 'error');
              }
            }}
          >
            Reset all data
          </Button>
        </div>
      </div>
    </div>
  );
}
